// @ts-nocheck - TypeScript false positives: imports used in JSX but not recognized due to verbatimModuleSyntax
import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
// @ts-expect-error - verbatimModuleSyntax false positive: icons used in JSX
import { Loader2, Building2, CheckCircle, Cookie } from 'lucide-react'
import { cn } from '@/lib/utils'

interface LocalSelectorModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (tenantId: string, slug: string) => void
}

export default function LocalSelectorModal({ open, onOpenChange, onSelect }: LocalSelectorModalProps) {
  const [locales, setLocales] = useState<Array<{ id: string; nombre: string; slug: string; color?: string; rol: string }>>([])
  const [cargando, setCargando] = useState(true)
  const [recordar, setRecordar] = useState(true)
  const [seleccionado, setSeleccionado] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      cargarLocales()
    }
  }, [open])

  async function cargarLocales() {
    setCargando(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('No hay sesión activa')
        onOpenChange(false)
        return
      }

      const { data, error } = await supabase
        .from('miembros')
        .select('tenant_id, rol, tenants!inner(id, nombre, slug, config)')
        .eq('user_id', session.user.id)

      if (error) throw error

      const localesData = (data || []).map((m: any) => ({
        id: m.tenant_id,
        nombre: m.tenants?.nombre || 'Sin nombre',
        slug: m.tenants?.slug || '',
        color: m.tenants?.config?.color || '#924e7d',
        rol: m.rol,
      }))

      setLocales(localesData)
    } catch (err) {
      console.error('Error cargando locales:', err)
      toast.error('Error cargando tus locales')
    } finally {
      setCargando(false)
    }
  }

  function handleSeleccionar(tenantId: string, slug: string) {
    setSeleccionado(tenantId)

    if (recordar) {
      // Guardar preferencia en localStorage y cookie
      localStorage.setItem('bh_ultimo_tenant', tenantId)
      document.cookie = `tenant_id=${tenantId}; Path=/; Domain=.beautyhub.com.ar; SameSite=Lax; Secure; Max-Age=${60 * 60 * 24 * 30}`
      document.cookie = `tenant_slug=${slug}; Path=/; Domain=.beautyhub.com.ar; SameSite=Lax; Secure; Max-Age=${60 * 60 * 24 * 30}`
    }

    // Cerrar modal y ejecutar callback
    onOpenChange(false)
    onSelect(tenantId, slug)
  }

  function handleContinuarSinRecordar(tenantId: string, slug: string) {
    setRecordar(false)
    handleSeleccionar(tenantId, slug)
  }

  if (!open) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-center">
          <Building2 className="h-12 w-12 text-primary mx-auto mb-3" />
          <DialogTitle>¿En qué local trabajas hoy?</DialogTitle>
          <DialogDescription>
            Tenés acceso a {locales.length} local{locales.length > 1 ? 'es' : ''}. Elegí uno para entrar.
          </DialogDescription>
        </DialogHeader>

        {cargando ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : locales.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No tenés locales asignados. Contactá al administrador.
          </div>
        ) : (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {locales.map((local) => (
              <Button
                key={local.id}
                variant={seleccionado === local.id ? 'default' : 'outline'}
                className={cn(
                  'w-full justify-start gap-3 p-4',
                  'hover:shadow-md transition-shadow',
                  'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95'
                )}
                onClick={() => handleSeleccionar(local.id, local.slug)}
                disabled={cargando}
              >
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: local.color }}
                >
                  <Building2 className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="font-medium truncate">{local.nombre}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {local.rol === 'owner' ? 'Dueño' : local.rol === 'admin' ? 'Admin' : 'Empleado'}
                  </p>
                </div>
                {seleccionado === local.id && (
                  <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                )}
              </Button>
            ))}

            <div className="flex items-center gap-2 p-2 text-xs text-muted-foreground">
              <Cookie className="h-3.5 w-3.5 flex-shrink-0" />
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={recordar}
                  onChange={(e) => setRecordar(e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                Recordar esta elección (30 días)
              </label>
            </div>
          </div>
        )}

        {seleccionado && !cargando && (
          <DialogFooter className="justify-center pt-2">
            <Button onClick={() => {
              const local = locales.find(l => l.id === seleccionado)
              if (local) handleContinuarSinRecordar(local.id, local.slug)
            }} className="w-full">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Entrando a {locales.find(l => l.id === seleccionado)?.nombre}...
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}